/**
 * 🤖 Multi-LLM Provider Manager for Ekamanam
 *
 * Supports multiple LLM providers with automatic fallback:
 * - Google Gemini (primary)
 * - Groq (fast, generous free tier)
 * - Perplexity (web-connected research)
 * - Claude / Anthropic (most accurate, technically precise)
 *
 * v7.1.0: Intelligent AI caching via Google Drive
 * - Caches responses per PDF page to avoid redundant API calls
 * - Similarity matching finds related cached queries
 * - Significant cost savings and faster responses
 */

import { hasDrivePermissions } from './googleDriveService';
import {
  getPageCache,
  saveToCache,
  findSimilarQuery
} from './aiCacheService';
import secureStorage from '../utils/secureStorage';
import logger from '../utils/logger';
import {
  buildSystemPrompt,
  getUserLearningProfile
} from './tutorPersonaService';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';

// Per-user daily cap on calls that fall back to the app's shared/default API
// keys (not the user's own). Keeps the app owner's free-tier keys from being
// exhausted as signup grows. Must match firestore.rules `defaultKeyUsage` cap.
const DEFAULT_KEY_DAILY_CAP = 25;

// ===== PROVIDER DEFINITIONS =====
export const PROVIDERS = {
  OPENROUTER: 'openrouter', // Default — aggregator with 300+ models, pay-per-use
  GEMINI: 'gemini',
  GROQ: 'groq',
  PERPLEXITY: 'perplexity',
  CLAUDE: 'claude',         // Anthropic — most accurate, technically precise
  SARVAM: 'sarvam'          // Indian language specialist (Hindi, Telugu, Tamil, etc.)
};

const PROVIDER_ENDPOINTS = {
  [PROVIDERS.OPENROUTER]: 'https://openrouter.ai/api/v1/chat/completions',
  [PROVIDERS.GEMINI]: 'https://generativelanguage.googleapis.com/v1beta/models/',
  [PROVIDERS.GROQ]: 'https://api.groq.com/openai/v1/chat/completions',
  [PROVIDERS.PERPLEXITY]: 'https://api.perplexity.ai/chat/completions',
  [PROVIDERS.CLAUDE]: 'https://api.anthropic.com/v1/messages',
  [PROVIDERS.SARVAM]: 'https://api.sarvam.ai/v1/chat/completions'
};

// Helper: Detect if text contains regional Indian languages (Unicode detection)
const hasRegionalLanguage = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Check for Indian regional language Unicode ranges
  const hasDevanagari = /[\u0900-\u097F]/.test(text); // Hindi, Sanskrit, Marathi
  const hasTelugu = /[\u0C00-\u0C7F]/.test(text);
  const hasTamil = /[\u0B80-\u0BFF]/.test(text);
  const hasBengali = /[\u0980-\u09FF]/.test(text);
  const hasGujarati = /[\u0A80-\u0AFF]/.test(text);
  const hasGurmukhi = /[\u0A00-\u0A7F]/.test(text); // Punjabi
  const hasOriya = /[\u0B00-\u0B7F]/.test(text); // Odia
  const hasMalayalam = /[\u0D00-\u0D7F]/.test(text);
  const hasKannada = /[\u0C80-\u0CFF]/.test(text);
  
  return hasDevanagari || hasTelugu || hasTamil || hasBengali || hasGujarati || 
         hasGurmukhi || hasOriya || hasMalayalam || hasKannada;
};

// Default model IDs — override via localStorage keys: groq_model, claude_model, openrouter_model
const MODEL_DEFAULTS = {
  GEMINI: 'gemini-2.5-flash',
  GROQ: 'qwen/qwen3.6-27b',
  GROQ_FALLBACKS: ['openai/gpt-oss-120b', 'llama-3.3-70b-versatile'],
  CLAUDE: 'claude-sonnet-5',
  OPENROUTER: 'google/gemini-2.5-flash',
};

// Recommended free-tier providers for student onboarding (no credit card)
export const RECOMMENDED_FREE_PROVIDERS = [
  PROVIDERS.GEMINI,
  PROVIDERS.GROQ,
  PROVIDERS.CLAUDE,
];

// Feature-to-Provider mapping (with fallbacks)
// v16.7: Free-tier providers first — Gemini (generous limits), Groq (speed), Claude (quality)
// V3.0.3: For REGIONAL LANGUAGES (Telugu/Hindi/Tamil), Gemini is auto-promoted to first
const FEATURE_PROVIDERS = {
  teacherMode:    [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  explain:        [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  activities:     [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  resources:      [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.PERPLEXITY, PROVIDERS.OPENROUTER],
  wordAnalysis:   [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  examPrep:       [PROVIDERS.GROQ, PROVIDERS.GEMINI, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  longAnswer:     [PROVIDERS.GROQ, PROVIDERS.GEMINI, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  quizEvaluation: [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER],
  general:        [PROVIDERS.GEMINI, PROVIDERS.GROQ, PROVIDERS.CLAUDE, PROVIDERS.SARVAM, PROVIDERS.OPENROUTER]
};

// ===== MAIN API CALL FUNCTION =====
export async function callLLM(prompt, config = {}) {
  const {
    feature = 'general',
    temperature = 0.7,
    maxTokens = 8192, // V3.2: Increased from 4096 for better Telugu/regional language support
    systemPrompt = null,
    preferredProvider = null, // Allow manual override
    // v7.1.0: Cache parameters
    pdfId = null,
    pdfName = null,
    page = null,
    pageContent = null,
    context = null, // Selected text
    skipCache = false, // Force fresh API call
    imageData = null, // base64 string for Gemini Vision (no data: prefix)
    imageMimeType = 'image/jpeg' // MIME type of the image
  } = config;

  // v16.3.6: Diagnostics logged only once per session (not on every LLM call)
  if (!callLLM._diagsDone) {
    callLLM._diagsDone = true;
    console.log('📱 Device:', navigator.platform, '|', /Android/i.test(navigator.userAgent) ? 'Android' : /Chrome/i.test(navigator.userAgent) ? 'Chrome' : 'Other');
    try {
      const testKey = '_ekamanam_storage_test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (storageError) {
      throw new Error('Browser storage is disabled or unavailable. Please enable cookies and site data in your browser settings, then refresh the page.');
    }
  }

  // v7.1.0: Check cache first (if Drive connected and cache params provided)
  if (!skipCache && pdfId && page && hasDrivePermissions()) {
    try {
      console.log(`💾 [${feature}] Checking cache for PDF ${pdfId}, page ${page}...`);
      const cachedData = await getPageCache(pdfId, page);

      if (cachedData && cachedData.queries.length > 0) {
        // Look for similar query in cache
        const similarQuery = findSimilarQuery(
          cachedData.queries,
          prompt,
          context || '',
          0.7 // 70% similarity threshold
        );

        if (similarQuery) {
          const matchPercentage = similarQuery.similarity ? Math.round(similarQuery.similarity * 100) : 0;
          console.log(`✅ [${feature}] Cache HIT! Using cached response (${matchPercentage}% match)`);
          return {
            response: similarQuery.response,
            fromCache: true,
            cacheMatch: similarQuery.similarity,
            tokensUsed: 0 // No API call
          };
        } else {
          console.log(`ℹ️ [${feature}] Cache MISS - no similar query found`);
        }
      }
    } catch (cacheError) {
      console.warn('⚠️ Cache check failed (continuing with API call):', cacheError);
      // Continue to API call
    }
  }

  // v11.10.0: AI Tutor Personality - Build personalized system prompt
  let finalSystemPrompt = systemPrompt;

  if (!systemPrompt && currentUserId) {
    try {
      // Fetch user's learning profile
      const profile = await getUserLearningProfile(currentUserId);

      // Build personalized system prompt based on persona and learning style
      finalSystemPrompt = buildSystemPrompt(
        profile.personaId,
        profile.learningStyleId,
        {
          subject: context?.subject || null,
          gradeLevel: context?.gradeLevel || null,
          languagePreference: context?.languagePreference || null
        }
      );

      logger.info(`🎭 Using ${profile.personaId} persona` +
        (profile.learningStyleId ? ` with ${profile.learningStyleId} learning style` : ''));
    } catch (error) {
      logger.warn('Could not load learning profile, using default:', error);
      // Continue without custom prompt
    }
  }

  // Get provider list for this feature
  let providers = preferredProvider
    ? [preferredProvider, ...(FEATURE_PROVIDERS[feature] || [PROVIDERS.GEMINI])]
    : (FEATURE_PROVIDERS[feature] || [PROVIDERS.GEMINI]);

  // V3.0.3: AUTO-DETECT REGIONAL LANGUAGES → PRIORITIZE GEMINI
  // Check if prompt contains Telugu/Hindi/Tamil/etc Unicode characters
  if (!preferredProvider && hasRegionalLanguage(prompt)) {
    // Swap provider order: Gemini first for regional languages
    const hasGroq = providers.includes(PROVIDERS.GROQ);
    const hasGemini = providers.includes(PROVIDERS.GEMINI);

    if (hasGroq && hasGemini) {
      // Swap: put Gemini before Groq
      providers = providers.filter(p => p !== PROVIDERS.GROQ && p !== PROVIDERS.GEMINI);
      providers.unshift(PROVIDERS.GEMINI, PROVIDERS.GROQ); // Gemini first!
      console.log(`🌐 [${feature}] Regional language detected → Using Gemini first`);
    }
  }

  // Remove duplicates
  providers = [...new Set(providers)];

  // Apply user provider preferences: drop disabled providers, promote the chosen primary
  const { primary, disabled } = getProviderPrefs();
  if (disabled.length) {
    providers = providers.filter(p => !disabled.includes(p));
  }
  if (primary && providers.includes(primary)) {
    providers = [primary, ...providers.filter(p => p !== primary)];
  }

  // If imageData is present, vision is only supported on Gemini — restrict to it
  if (imageData) {
    if (disabled.includes(PROVIDERS.GEMINI)) {
      throw new Error('Image analysis requires Gemini Vision, which is disabled in Settings. Enable Gemini to use this feature.');
    }
    providers = [PROVIDERS.GEMINI];
    console.log('🖼️ Image data detected → restricting to Gemini Vision only');
  }

  if (providers.length === 0) {
    throw new Error('All AI providers are disabled. Enable at least one provider in Settings.');
  }

  // Try each provider in order (with fallback)
  let lastError = null;
  // Error from a provider that HAD a key configured but the call itself failed.
  // Prioritized when reporting the final failure — a real error (bad key, model
  // issue, rate limit, CORS) is far more useful to the user than "no key configured"
  // for some other provider further down the list they never set up.
  let lastRealError = null;

  for (let i = 0; i < providers.length; i++) {
    const provider = providers[i];
    try {
      // v16.3.6: Pre-check if provider has an API key — skip silently if not configured
      let hasKey = false;
      try { hasKey = await hasApiKey(provider); } catch { /* ignore */ }
      if (!hasKey) {
        lastError = new Error(`No API key configured for ${provider}`);
        continue; // silently skip to next provider
      }

      // v16.7.2: Cap shared/default-key usage per user per day — protects the
      // app owner's free-tier keys as signups grow. Only applies when the
      // user is riding the shared key, never their own.
      if (!(await hasOwnKey(provider))) {
        try {
          await consumeDefaultKeyQuota();
        } catch (quotaError) {
          if (quotaError.isQuotaError) {
            lastError = quotaError;
            continue; // try the next provider, in case the user has a key elsewhere
          }
          console.warn('⚠️ Default key quota check failed, proceeding without cap:', quotaError);
        }
      }

      const startTime = Date.now();
      let response = await callProvider(provider, prompt, {
        temperature,
        maxTokens,
        systemPrompt: finalSystemPrompt,
        imageData,
        imageMimeType,
      });
      const duration = Date.now() - startTime;

      console.log(`✅ [${feature}] ${provider} succeeded in ${duration}ms`);

      // Strip <think>...</think> blocks from reasoning models (DeepSeek, etc.)
      if (typeof response === 'string' && response.includes('<think>')) {
        response = response.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
      }

      // Track usage
      trackUsage(provider, feature, maxTokens, duration);

      // v7.1.0: Save to cache (if Drive connected and cache params provided)
      if (pdfId && page && pageContent && hasDrivePermissions()) {
        try {
          await saveToCache(
            pdfId,
            pdfName || 'Unknown PDF',
            page,
            pageContent,
            {
              question: prompt,
              mode: feature,
              context: context || '',
              response: typeof response === 'string' ? response : response.response,
              tokensUsed: maxTokens
            }
          );
          console.log(`💾 [${feature}] Response cached for future use`);
        } catch (cacheError) {
          console.warn('⚠️ Failed to save to cache:', cacheError);
          // Don't throw - API call succeeded
        }
      }

      return response;

    } catch (error) {
      lastError = error;
      lastRealError = error; // this provider had a key configured — its failure is the informative one
      console.error(`⚠️ [${feature}] ${provider} failed:`, error);
      // Continue to the next provider — but don't let a "no key" skip for a
      // later provider overwrite this real error when we report the failure below.
    }
  }

  // All providers exhausted. Prefer a real failure (a provider that was actually
  // configured and errored) over a generic "no key configured" message — that
  // distinction is what previously hid genuine provider errors (e.g. a bad or
  // rejected API key) behind a misleading "add your Gemini or Groq key" message
  // whenever Gemini (last in the priority list) also happened to be unconfigured.
  const finalError = lastRealError || lastError
    || new Error('AI service temporarily unavailable. Please check your internet connection and try again.');

  // v10.4.2: Better error messages for mobile browsers
  let errorMessage = finalError.message || 'Unknown error occurred';

  if (finalError.message?.includes('Failed to fetch') || finalError.message?.includes('NetworkError')) {
    errorMessage = 'Network connection issue. Please check your internet connection and try again.';
  } else if (finalError.message?.includes('No API key')) {
    errorMessage = 'No AI provider is configured. Please add an API key in Settings (Claude, OpenRouter, Gemini, or Groq).';
  } else if (finalError.isQuotaError || finalError.message?.includes('Daily free AI limit')) {
    errorMessage = 'You\'ve used today\'s free AI quota. Add your own free API key in Settings to keep going without limits.';
  } else if (finalError.message?.includes('safety filters')) {
    errorMessage = 'Content blocked by safety filters. Please rephrase your question.';
  } else if (finalError.message?.includes('truncated') || finalError.message?.includes('MAX_TOKENS')) {
    errorMessage = 'Response too long. Please ask a more specific question.';
  } else if (finalError.message?.includes('Invalid response')) {
    errorMessage = 'Received invalid response from AI service. Please try again.';
  }

  throw new Error(errorMessage);
}

// ===== PROVIDER ROUTER =====
async function callProvider(provider, prompt, config) {
  // Use secure storage to get API key
  const apiKey = await getApiKeySecure(provider);

  if (!apiKey) {
    throw new Error(`No API key configured for ${provider}. Please add it in Settings.`);
  }

  switch (provider) {
    case PROVIDERS.OPENROUTER:
      return callOpenRouter(prompt, apiKey, config);
    case PROVIDERS.GEMINI:
      return callGemini(prompt, apiKey, config);
    case PROVIDERS.GROQ:
      return callGroq(prompt, apiKey, config);
    case PROVIDERS.PERPLEXITY:
      return callPerplexity(prompt, apiKey, config);
    case PROVIDERS.CLAUDE:
      return callClaude(prompt, apiKey, config);
    case PROVIDERS.SARVAM:
      return callSarvam(prompt, apiKey, config);
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
}

// ===== PROVIDER PREFERENCES (primary provider + enable/disable) =====
const PROVIDER_PREFS_KEY = 'llm_provider_prefs';

function getProviderPrefs() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROVIDER_PREFS_KEY) || '{}');
    return {
      primary: parsed.primary || null,
      disabled: Array.isArray(parsed.disabled) ? parsed.disabled : []
    };
  } catch {
    return { primary: null, disabled: [] };
  }
}

function saveProviderPrefs(prefs) {
  localStorage.setItem(PROVIDER_PREFS_KEY, JSON.stringify(prefs));
}

/** The provider tried first for every feature, or null if none was chosen (default priority applies). */
export function getPrimaryProvider() {
  return getProviderPrefs().primary;
}

/** Set (or clear with a falsy value) the provider tried first for every feature. */
export function setPrimaryProvider(provider) {
  const prefs = getProviderPrefs();
  prefs.primary = provider || null;
  saveProviderPrefs(prefs);
}

/** Providers the user has turned off — never tried, not even as a fallback. */
export function getDisabledProviders() {
  return getProviderPrefs().disabled;
}

/** Enable or disable a provider. Disabling the current primary clears the primary too. */
export function setProviderDisabled(provider, isDisabled) {
  const prefs = getProviderPrefs();
  const set = new Set(prefs.disabled);
  if (isDisabled) set.add(provider); else set.delete(provider);
  prefs.disabled = [...set];
  if (isDisabled && prefs.primary === provider) prefs.primary = null;
  saveProviderPrefs(prefs);
}

export function isProviderDisabled(provider) {
  return getProviderPrefs().disabled.includes(provider);
}

/** Overwrite local prefs wholesale — used to apply prefs synced down from Firestore. */
export function applyProviderPrefs(prefs) {
  if (!prefs) return;
  saveProviderPrefs({
    primary: prefs.primary || null,
    disabled: Array.isArray(prefs.disabled) ? prefs.disabled : []
  });
}

// ===== API KEY MANAGEMENT =====
// SECURITY UPGRADE: Now supports encrypted storage
// Legacy keys in localStorage are automatically migrated on first use

let currentUserId = null; // Set by initializeSecureStorage()
let defaultKeys = {};    // In-memory defaults for free-tier evaluation (NOT persisted)

/**
 * Set a default API key for a provider.
 * Used for free/guest users so they can evaluate the app without entering their own key.
 * The key lives only in memory — it is never written to localStorage or Firestore.
 * @param {string} provider - Provider constant (e.g. PROVIDERS.OPENROUTER)
 * @param {string} key      - Default API key value
 */
export function setDefaultKey(provider, key) {
  defaultKeys[provider] = key;
}

/** Returns true if an in-memory default key is available for the given provider */
export function hasDefaultKey(provider) {
  return !!defaultKeys[provider];
}

/**
 * Load shared default API keys from Firestore appConfig/defaults.
 * Keys are session-only (never persisted to localStorage).
 * @param {Object} configData - The appConfig/defaults document data
 * @param {Object} userProviderKeys - User's own provider keys (skip defaults they already have)
 * @returns {string[]} List of providers for which default keys were loaded
 */
export function loadAppDefaultKeys(configData, userProviderKeys = {}) {
  if (!configData || typeof configData !== 'object') return [];

  const keyFieldMap = {
    [PROVIDERS.OPENROUTER]: 'openrouterKey',
    [PROVIDERS.GEMINI]: 'geminiKey',
    [PROVIDERS.GROQ]: 'groqKey',
    [PROVIDERS.CLAUDE]: 'claudeKey',
  };

  const loaded = [];
  for (const [provider, field] of Object.entries(keyFieldMap)) {
    if (userProviderKeys[provider]) continue;
    const keyValue = configData[field];
    if (keyValue && typeof keyValue === 'string' && keyValue.trim()) {
      setDefaultKey(provider, keyValue.trim());
      loaded.push(provider);
    }
  }
  return loaded;
}

/**
 * Check if any AI provider is configured (user key or shared default).
 * @returns {Promise<boolean>}
 */
export async function hasAnyAIConfigured() {
  const allProviders = [
    ...RECOMMENDED_FREE_PROVIDERS,
    PROVIDERS.OPENROUTER,
    PROVIDERS.PERPLEXITY,
    PROVIDERS.SARVAM,
  ];
  for (const provider of allProviders) {
    if (hasDefaultKey(provider)) return true;
    try {
      if (await hasApiKey(provider)) return true;
    } catch { /* ignore */ }
  }
  return false;
}

/**
 * Initialize secure storage for API keys
 * IMPORTANT: Call this when user logs in to enable encryption
 * @param {string} userId - User ID for encryption key derivation
 */
export async function initializeSecureStorage(userId) {
  if (!userId) {
    logger.warn('LLM Service: Cannot initialize secure storage without userId');
    return;
  }

  currentUserId = userId;
  logger.info('LLM Service: Secure storage initialized for user');

  // Auto-migrate existing plain-text keys to encrypted storage
  try {
    await secureStorage.migrateExistingKeys(userId);
  } catch (error) {
    logger.error('LLM Service: Failed to migrate keys to secure storage', error);
  }
}

/**
 * Get API key for provider
 * Checks secure storage first, falls back to localStorage for backward compatibility
 * @param {string} provider - Provider name
 * @returns {Promise<string|null>} API key or null
 */
async function getApiKeySecure(provider) {
  const keyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_api_key',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  const legacyKeyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_pat',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  // Try secure storage first (if user is logged in)
  if (currentUserId) {
    try {
      const secureKey = await secureStorage.getItem(keyMap[provider], currentUserId);
      if (secureKey) {
        return secureKey;
      }
    } catch (error) {
      logger.error(`Failed to get ${provider} key from secure storage:`, error);
      // Fall through to localStorage check
    }
  }

  // Fallback to localStorage (legacy/backward compatibility)
  const legacyKey = localStorage.getItem(legacyKeyMap[provider]);
  if (legacyKey) {
    // If we have a userId, migrate this key to secure storage
    if (currentUserId) {
      try {
        await secureStorage.setItem(keyMap[provider], legacyKey, currentUserId);
        localStorage.removeItem(legacyKeyMap[provider]);
        logger.info(`Migrated ${provider} key to secure storage`);
        return legacyKey;
      } catch (error) {
        logger.error(`Failed to migrate ${provider} key:`, error);
      }
    }
    return legacyKey;
  }

  // Fallback to in-memory default key (for free users — loaded from Firestore appConfig, session-only)
  if (defaultKeys[provider]) {
    return defaultKeys[provider];
  }

  return null;
}

/**
 * Whether the user has their own key for this provider (secure storage or
 * legacy localStorage) — i.e. calls would NOT fall back to the shared default
 * key. Used to decide whether the default-key daily cap applies.
 */
async function hasOwnKey(provider) {
  const keyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_api_key',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };
  const legacyKeyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_pat',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  if (currentUserId) {
    try {
      const secureKey = await secureStorage.getItem(keyMap[provider], currentUserId);
      if (secureKey) return true;
    } catch { /* fall through */ }
  }
  return !!localStorage.getItem(legacyKeyMap[provider]);
}

/**
 * Enforces DEFAULT_KEY_DAILY_CAP per signed-in user per day for calls that
 * would use the app's shared default key. Throws { isQuotaError: true } once
 * the cap is hit. Firestore rules (`defaultKeyUsage/{uid}/days/{date}`)
 * independently enforce the same cap server-side, so this can't be bypassed
 * by clearing localStorage.
 */
async function consumeDefaultKeyQuota() {
  if (!currentUserId) return; // default keys are only loaded for signed-in users

  const dayId = new Date().toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
  const ref = doc(db, 'defaultKeyUsage', currentUserId, 'days', dayId);
  const snap = await getDoc(ref);
  const count = snap.exists() ? (snap.data().count || 0) : 0;

  if (count >= DEFAULT_KEY_DAILY_CAP) {
    const err = new Error('Daily free AI limit reached. Add your own API key in Settings to keep going.');
    err.isQuotaError = true;
    throw err;
  }

  if (snap.exists()) {
    await updateDoc(ref, { count: increment(1) });
  } else {
    await setDoc(ref, { count: 1, date: dayId });
  }
}

/**
 * Synchronous version for backward compatibility
 * WARNING: This only checks localStorage, not secure storage
 * Use getApiKeySecure() for full security
 * @deprecated Use getApiKeySecure() instead
 */
function getApiKey(provider) {
  const keyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_pat',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  return localStorage.getItem(keyMap[provider]) || null;
}

/**
 * Set API key for provider
 * Automatically uses secure storage if user is logged in
 * @param {string} provider - Provider name
 * @param {string} key - API key
 */
export async function setApiKey(provider, key) {
  const keyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_api_key',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  const legacyKeyMap = {
    [PROVIDERS.OPENROUTER]: 'openrouter_api_key',
    [PROVIDERS.GEMINI]: 'gemini_pat',
    [PROVIDERS.GROQ]: 'groq_api_key',
    [PROVIDERS.PERPLEXITY]: 'perplexity_api_key',
    [PROVIDERS.CLAUDE]: 'claude_api_key',
    [PROVIDERS.SARVAM]: 'sarvam_api_key'
  };

  if (key && key.trim()) {
    // Use secure storage if user is logged in
    if (currentUserId) {
      try {
        await secureStorage.setItem(keyMap[provider], key.trim(), currentUserId);
        // Remove legacy key if exists
        localStorage.removeItem(legacyKeyMap[provider]);
        logger.info(`Stored ${provider} key in secure storage`);
        return;
      } catch (error) {
        logger.error(`Failed to store ${provider} key in secure storage:`, error);
        // Fall back to localStorage
      }
    }

    // Fallback to localStorage (if no user or secure storage failed)
    localStorage.setItem(legacyKeyMap[provider], key.trim());
    logger.warn(`Stored ${provider} key in localStorage (not encrypted)`);
  } else {
    // Remove key
    if (currentUserId) {
      secureStorage.removeItem(keyMap[provider]);
    }
    localStorage.removeItem(legacyKeyMap[provider]);
  }
}

/**
 * Check if API key exists for provider
 * @param {string} provider - Provider name
 * @returns {Promise<boolean>}
 */
export async function hasApiKey(provider) {
  const key = await getApiKeySecure(provider);
  return !!key;
}

/**
 * Get list of providers with API keys configured
 * @returns {Promise<string[]>}
 */
export async function getAvailableProviders() {
  const providers = await Promise.all(
    Object.values(PROVIDERS).map(async provider => {
      const hasKey = await hasApiKey(provider);
      return hasKey ? provider : null;
    })
  );
  return providers.filter(p => p !== null);
}

// ===== PROVIDER IMPLEMENTATIONS =====

/**
 * Google Gemini API
 */
async function callGemini(prompt, apiKey, config) {
  const model = MODEL_DEFAULTS.GEMINI;
  const url = `${PROVIDER_ENDPOINTS[PROVIDERS.GEMINI]}${model}:generateContent?key=${apiKey}`;

  // v10.4.2: Add timeout and better error handling for mobile browsers
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

  try {
    // v11.10.0: Build request body with optional system instruction
    // v11.12.x: Support Gemini Vision — prepend image inlineData when provided
    const requestParts = [{ text: prompt }];
    if (config.imageData) {
      requestParts.unshift({
        inlineData: {
          mimeType: config.imageMimeType || 'image/jpeg',
          data: config.imageData,
        }
      });
    }
    const requestBody = {
      contents: [{ parts: requestParts }],
      generationConfig: {
        temperature: config.temperature,
        maxOutputTokens: config.maxTokens
      }
    };

    // Add system instruction if provided (Gemini API format)
    if (config.systemPrompt) {
      requestBody.systemInstruction = {
        parts: [{ text: config.systemPrompt }]
      };
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = 'Gemini API failed';
      try {
        const error = await response.json();
        errorMessage = error.error?.message || errorMessage;
      } catch (e) {
        // If JSON parsing fails, use status text
        errorMessage = `Gemini API failed: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
  
  // Validate response
  if (!data.candidates || !data.candidates[0]) {
    throw new Error('Invalid response from Gemini');
  }
  
  const candidate = data.candidates[0];
  
  // Check for safety blocks
  if (candidate.finishReason === 'SAFETY') {
    throw new Error('Content blocked by safety filters');
  }
  
  if (candidate.finishReason === 'MAX_TOKENS') {
    console.warn('⚠️ Gemini response truncated due to token limit');
    // Only salvage truncated HTML responses (labs, slides rendered in iframes).
    // JSON responses MUST NOT be salvaged — truncated JSON is unparseable.
    const partialParts = candidate.content?.parts;
    if (partialParts?.length && partialParts[0]?.text?.length > 200) {
      const partialText = partialParts.map(p => p.text || '').join('');
      const looksLikeHTML = /<!doctype|<html/i.test(partialText);
      if (looksLikeHTML) {
        console.warn('⚠️ Returning truncated HTML Gemini response (' + partialText.length + ' chars)');
        return partialText;
      }
    }
    throw new Error('Response truncated - increase maxOutputTokens');
  }
  
    // Extract text from parts (handle single or multiple parts)
    const parts = candidate.content.parts;
    if (!parts || parts.length === 0) {
      throw new Error('No text in Gemini response');
    }
    
    // If multiple parts, join them
    if (parts.length > 1) {
      return parts.map(part => part.text || '').join('\n');
    }
    
    return parts[0].text || '';
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection and try again.');
    }
    throw error;
  }
}

/**
 * Groq API (OpenAI-compatible, ultra-fast)
 * v16.7: Qwen3.6-27B primary with fallback chain for deprecated models
 */
async function callGroq(prompt, apiKey, config) {
  const customModel = localStorage.getItem('groq_model');
  const models = customModel
    ? [customModel]
    : [MODEL_DEFAULTS.GROQ, ...MODEL_DEFAULTS.GROQ_FALLBACKS];

  const messages = [
    ...(config.systemPrompt ? [{ role: 'system', content: config.systemPrompt }] : []),
    { role: 'user', content: prompt }
  ];

  let lastError = null;
  for (const model of models) {
    try {
      const response = await fetch(PROVIDER_ENDPOINTS[PROVIDERS.GROQ], {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model,
          messages,
          temperature: config.temperature,
          max_tokens: config.maxTokens
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const msg = error.error?.message || `Groq API failed (${response.status})`;
        console.warn(`⚠️ Groq model ${model} failed: ${msg}`);
        lastError = new Error(msg);
        continue;
      }

      const data = await response.json();
      if (!data.choices?.[0]) {
        lastError = new Error('Invalid response from Groq');
        continue;
      }

      if (model !== models[0]) {
        console.log(`✅ Groq fallback model ${model} succeeded`);
      }
      return data.choices[0].message.content;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('All Groq models failed');
}

/**
 * Perplexity API (Web-connected, with citations)
 */
async function callPerplexity(prompt, apiKey, config) {
  const model = 'llama-3.1-sonar-large-128k-online'; // Web-connected
  
  const messages = [
    { 
      role: 'system', 
      content: config.systemPrompt || 'You are a helpful educational assistant with access to the web. Provide citations when possible.'
    },
    { role: 'user', content: prompt }
  ];
  
  const response = await fetch(PROVIDER_ENDPOINTS[PROVIDERS.PERPLEXITY], {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: model,
      messages: messages,
      temperature: config.temperature,
      max_tokens: config.maxTokens,
      search_recency_filter: 'month', // Use recent web data
      return_citations: true
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || `Perplexity API failed (${response.status})`);
  }
  
  const data = await response.json();
  
  if (!data.choices || !data.choices[0]) {
    throw new Error('Invalid response from Perplexity');
  }
  
  // Perplexity includes citations in the response
  return data.choices[0].message.content;
}

/**
 * Claude (Anthropic) API
 * Requires 'anthropic-dangerous-direct-browser-access' header for direct browser calls
 */
async function callClaude(prompt, apiKey, config) {
  const model = localStorage.getItem('claude_model') || MODEL_DEFAULTS.CLAUDE;

  // v10.4.2-style: Add timeout and better error handling for mobile browsers
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

  try {
    const body = {
      model,
      max_tokens: config.maxTokens,
      messages: [{ role: 'user', content: prompt }]
    };
    if (config.systemPrompt) {
      body.system = config.systemPrompt;
    }

    const response = await fetch(PROVIDER_ENDPOINTS[PROVIDERS.CLAUDE], {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'content-type': 'application/json',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify(body),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || `Claude API failed (${response.status})`);
    }

    const data = await response.json();

    if (!data.content || !data.content.length) {
      throw new Error('Invalid response from Claude');
    }

    if (data.stop_reason === 'refusal') {
      throw new Error('Content blocked by Claude safety filters');
    }

    if (data.content.length > 1) {
      return data.content
        .filter(block => block.type === 'text')
        .map(block => block.text)
        .join('\n');
    }

    return data.content[0].text;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your internet connection and try again.');
    }
    throw error;
  }
}

/**
 * Sarvam AI (Indian language specialist — Hindi, Telugu, Tamil, Kannada, etc.)
 * Auth: api-subscription-key header (NOT Bearer)
 * Models: sarvam-m (primary), saaras:v3
 */
async function callSarvam(prompt, apiKey, config) {
  const model = 'sarvam-m';

  const messages = [
    ...(config.systemPrompt ? [{ role: 'system', content: config.systemPrompt }] : []),
    { role: 'user', content: prompt }
  ];

  const response = await fetch(PROVIDER_ENDPOINTS[PROVIDERS.SARVAM], {
    method: 'POST',
    headers: {
      'api-subscription-key': apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: model,
      messages: messages,
      temperature: config.temperature,
      max_tokens: config.maxTokens
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `Sarvam AI failed (${response.status})`);
  }

  const data = await response.json();

  if (!data.choices?.[0]) {
    throw new Error('Invalid response from Sarvam AI');
  }

  return data.choices[0].message.content;
}

/**
 * OpenRouter API (aggregator — 300+ models, OpenAI-compatible)
 * Default model: google/gemini-2.0-flash-001 (fast, multilingual, affordable)
 * Users can set OPENROUTER_MODEL in localStorage to override
 */
async function callOpenRouter(prompt, apiKey, config) {
  const model = localStorage.getItem('openrouter_model') || MODEL_DEFAULTS.OPENROUTER;

  const messages = [
    ...(config.systemPrompt ? [{ role: 'system', content: config.systemPrompt }] : []),
    { role: 'user', content: prompt }
  ];

  const response = await fetch(PROVIDER_ENDPOINTS[PROVIDERS.OPENROUTER], {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': 'https://ekamanam.com',
      'X-Title': 'Ekamanam Learning'
    },
    body: JSON.stringify({
      model,
      messages,
      temperature: config.temperature,
      max_tokens: config.maxTokens
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `OpenRouter API failed (${response.status})`);
  }

  const data = await response.json();

  if (!data.choices?.[0]) {
    throw new Error('Invalid response from OpenRouter');
  }

  return data.choices[0].message.content;
}

// ===== USAGE TRACKING =====
function trackUsage(provider, feature, tokens, duration) {
  try {
    const stats = JSON.parse(localStorage.getItem('llm_usage') || '{}');
    
    if (!stats[provider]) {
      stats[provider] = { requests: 0, tokens: 0, totalDuration: 0, features: {} };
    }
    
    stats[provider].requests++;
    stats[provider].tokens += tokens;
    stats[provider].totalDuration += duration;
    
    if (!stats[provider].features[feature]) {
      stats[provider].features[feature] = 0;
    }
    stats[provider].features[feature]++;
    
    localStorage.setItem('llm_usage', JSON.stringify(stats));
  } catch (error) {
    console.error('Failed to track usage:', error);
  }
}

export function getUsageStats() {
  try {
    return JSON.parse(localStorage.getItem('llm_usage') || '{}');
  } catch {
    return {};
  }
}

export function clearUsageStats() {
  localStorage.removeItem('llm_usage');
}

// ===== TEXT-TO-SPEECH via Gemini TTS =====

// TTS models to try in order (fallback chain)
const TTS_MODELS = [
  { model: 'gemini-2.5-flash-preview-tts', endpoint: 'v1alpha' },
  { model: 'gemini-2.0-flash', endpoint: 'v1beta' },
  { model: 'gemini-2.0-flash-001', endpoint: 'v1beta' },
];

/**
 * Generate audio from text using Gemini's TTS capabilities.
 * Tries multiple model versions as fallback.
 *
 * @param {string} text - The text to convert to speech
 * @param {string} voiceName - Gemini voice: Aoede, Charon, Fenrir, Kore, Puck (default: Kore)
 * @returns {Promise<{ audioBase64: string, mimeType: string }>}
 */
export async function generateTTSAudio(text, voiceName = 'Kore') {
  const apiKey = await getApiKeySecure(PROVIDERS.GEMINI);
  if (!apiKey) throw new Error('Gemini API key not configured. Please add it in Settings.');

  let lastError = null;

  for (const { model, endpoint } of TTS_MODELS) {
    try {
      console.log(`🔊 [TTS] Trying ${model} (${endpoint})...`);
      const url = `https://generativelanguage.googleapis.com/${endpoint}/models/${model}:generateContent?key=${apiKey}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text }]
          }],
          generationConfig: {
            responseModalities: ['AUDIO'],
            speechConfig: {
              voiceConfig: {
                prebuiltVoiceConfig: { voiceName }
              }
            }
          }
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = err.error?.message || `HTTP ${response.status}`;
        console.warn(`❌ [TTS] ${model} failed: ${msg}`);
        lastError = new Error(msg);
        continue; // try next model
      }

      const data = await response.json();
      const candidate = data.candidates?.[0];
      if (!candidate?.content?.parts?.length) {
        console.warn(`❌ [TTS] ${model}: no audio parts in response`);
        lastError = new Error('No audio returned');
        continue;
      }

      const audioPart = candidate.content.parts.find(p => p.inlineData);
      if (!audioPart?.inlineData?.data) {
        console.warn(`❌ [TTS] ${model}: no inlineData in response`);
        lastError = new Error('No audio data in response');
        continue;
      }

      console.log(`✅ [TTS] ${model} succeeded`);
      return {
        audioBase64: audioPart.inlineData.data,
        mimeType: audioPart.inlineData.mimeType || 'audio/wav'
      };
    } catch (err) {
      console.warn(`❌ [TTS] ${model} error:`, err.message);
      lastError = err;
      continue;
    }
  }

  throw lastError || new Error('All TTS models failed. Please try again.');
}

// ===== PROVIDER INFO =====
export const PROVIDER_INFO = {
  [PROVIDERS.GEMINI]: {
    name: 'Google Gemini 2.5 Flash',
    description: 'Most generous free tier — 1M token context, excellent for CBSE content and regional languages (Hindi, Telugu, Tamil)',
    freeLimit: '1,500 requests/day — no credit card',
    signupUrl: 'https://aistudio.google.com/app/apikey',
    status: 'recommended',
    badge: '⭐ Recommended',
    defaultModel: MODEL_DEFAULTS.GEMINI,
  },
  [PROVIDERS.GROQ]: {
    name: 'Groq (Qwen3.6)',
    description: 'Sub-second inference — fastest responses for study sessions. Uses Qwen3.6-27B for strong reasoning.',
    freeLimit: 'Generous free tier — no credit card',
    signupUrl: 'https://console.groq.com/keys',
    status: 'recommended',
    badge: '⚡ Fastest',
    defaultModel: MODEL_DEFAULTS.GROQ,
  },
  [PROVIDERS.CLAUDE]: {
    name: 'Claude Sonnet 5',
    description: 'Best reasoning for CBSE math and science — adaptive thinking, strong instruction-following',
    freeLimit: 'API credits on signup — console.anthropic.com',
    signupUrl: 'https://console.anthropic.com/settings/keys',
    status: 'recommended',
    badge: '🎯 Best Quality',
    defaultModel: MODEL_DEFAULTS.CLAUDE,
  },
  [PROVIDERS.OPENROUTER]: {
    name: 'OpenRouter',
    description: 'Access 300+ AI models via a single key — useful as a fallback aggregator',
    freeLimit: 'Pay-per-use (~$0.001–0.01/query)',
    signupUrl: 'https://openrouter.ai/keys',
    status: 'optional',
    badge: '🌐 Aggregator',
    defaultModel: MODEL_DEFAULTS.OPENROUTER,
  },
  [PROVIDERS.PERPLEXITY]: {
    name: 'Perplexity AI',
    description: 'Web-connected with citations',
    freeLimit: '5 searches/day (limited)',
    signupUrl: 'https://www.perplexity.ai/settings/api',
    status: 'optional',
    badge: '🔍 Research'
  },
  [PROVIDERS.SARVAM]: {
    name: 'Sarvam AI',
    description: 'Specialized for Indian languages — Hindi, Telugu, Tamil, Kannada, Bengali, and more. Built in India for Bharat.',
    freeLimit: 'Free tier available',
    signupUrl: 'https://dashboard.sarvam.ai/',
    status: 'optional',
    badge: '🇮🇳 Indic'
  }
};

export default {
  callLLM,
  setApiKey,
  hasApiKey,
  hasAnyAIConfigured,
  hasDefaultKey,
  loadAppDefaultKeys,
  getAvailableProviders,
  getUsageStats,
  clearUsageStats,
  getPrimaryProvider,
  setPrimaryProvider,
  getDisabledProviders,
  setProviderDisabled,
  isProviderDisabled,
  applyProviderPrefs,
  PROVIDERS,
  PROVIDER_INFO,
  RECOMMENDED_FREE_PROVIDERS,
};

